import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckboxInput from '../../form/CheckboxInput';
import { useState } from 'react';
import { ScrollableContent } from '../../ScrollableContent';
import { ProgressDialog } from '../../dialogs/ProgressDialog';
import { FullScreenDialogTitle } from '../../dialogs/FullScreenDialogTitle';
import { FullScreenDialogContent } from '../../dialogs/FullScreenDialogContent';
const PREFIX = 'ConfirmAttestationDialog';

const classes = {
  contentTitle: `${PREFIX}-contentTitle`,
  actionButtons: `${PREFIX}-actionButtons`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.contentTitle}`]: {
    fontFamily: 'Bitter',
    fontSize: '30px',
    fontWeight: '700',
    lineHeight: '40px',
    letterSpacing: '0.25px',
    textAlign: 'left',
    color: 'rgba(0, 0, 0, 1)',
  },
  [`& .${classes.actionButtons}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
  },
}));

type ConfirmAttestationDialogProps = {
  open: boolean;
  termsContent: string;
  close: () => void;
  onSubmit: () => void;
  attestationQueueLength: number;
  attestationQueueTotal: number;
};

const ConfirmAttestationDialog = ({
  open,
  termsContent,
  close,
  onSubmit,
  attestationQueueLength,
  attestationQueueTotal,
}: ConfirmAttestationDialogProps) => {
  const { t } = useTranslation('common');
  const [disabledConfirmAttestation, setDisabledConfirmAttestation] =
    useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submittingDialogOpen, setSubmittingDialogOpen] = useState(false);

  const formHook = useForm({
    mode: 'onTouched',
    defaultValues: {
      confirmAttestation: false,
    },
  });

  const termsConfirmed = formHook.watch('confirmAttestation');

  const handleSubmit = () => {
    setSubmitting(true);
    setSubmittingDialogOpen(true);
    onSubmit();
  };

  const handleClose = () => {
    setSubmitting(false);
    setSubmittingDialogOpen(false);
    formHook.reset();
    close();
  };

  const handleTermsScrollComplete = () => {
    setDisabledConfirmAttestation(false);
  };

  return (
    <StyledDialog fullScreen open={open} onClose={handleClose}>
      {submitting && <LinearProgress />}
      <FullScreenDialogTitle
        title={t('attestation.dialogTitle')}
        showClose={!submitting}
        close={handleClose}
      />
      <FullScreenDialogContent>
        <Typography className={classes.contentTitle}>
          {t('generateInvoice.tripConfirmationAndComplianceAttestation')}
        </Typography>
        <ScrollableContent handleScrollComplete={handleTermsScrollComplete}>
          <div dangerouslySetInnerHTML={{ __html: termsContent }} />
        </ScrollableContent>

        <CheckboxInput
          label={t('attestation.confirmAttestation')}
          name="confirmAttestation"
          disabled={disabledConfirmAttestation || submitting}
          required={true}
          control={formHook.control}
        />

        <Box className={classes.actionButtons}>
          <Button
            variant="contained"
            color="primary"
            disabled={!termsConfirmed || submitting}
            onClick={handleSubmit}>
            {t('common.submit')}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={submitting}
            onClick={handleClose}>
            {t('common.cancel')}
          </Button>
        </Box>
      </FullScreenDialogContent>
      {submittingDialogOpen && (
        <ProgressDialog
          title={t('attestation.generatingInvoice')}
          content={
            <>
              {t('attestation.pleaseDoNotClose')}
              <br />
              {t('attestation.processingProgress', {
                progress: attestationQueueTotal - attestationQueueLength,
                total: attestationQueueTotal,
              })}
            </>
          }
          open={submittingDialogOpen}
        />
      )}
    </StyledDialog>
  );
};

export { ConfirmAttestationDialog };
